import { Button, ErrorBoundary, Stack, Text, VStack } from '@genial/components';
import { BoxRadius } from '@genial/design-tokens';
import { Box, HStack, View } from 'native-base';
import React from 'react';

import { TermsAndDocuments } from '../../TermAcceptance/components/TermsAndDocuments';
import { TermAcceptanceLoadingDesktop } from '../../TermAcceptanceLoading/desktop';

import { TermActionViewProps } from './types';

export const TermActionDesktop: React.FC<TermActionViewProps> = ({
  termData,
  content,
  loading,
  error,
  retry,
  handleAccept,
  handleRefuse,
  handleDownloadTerm,
}) => {
  if (loading) {
    return <TermAcceptanceLoadingDesktop />;
  }

  if (error) {
    return (
      <Stack py="stack.lg">
        <ErrorBoundary onRefresh={retry} {...error} />;
      </Stack>
    );
  }

  if (!termData) {
    return (
      <Stack py="stack.lg">
        <ErrorBoundary
          onRefresh={retry}
          errorIconOrIllustration="NotEmptyIllustration"
          errorTitle="Termo não encontrado"
          errorMessage="Não foi possível carregar os dados deste termo."
        />
      </Stack>
    );
  }

  return (
    <View minW="container" maxWidth={900} flex={1}>
      <VStack p="inline.xxxs" space="inline.xxs" flex={1}>
        <VStack space="stack.xxxs">
          <Text variant="semibold" fontSize="xl">
            {content.title}
          </Text>
          <Box h="1px" bg="neutral.lightest" />
          <VStack space="stack.nano">
            {content.headerInfoItems.map((item, index) => (
              <React.Fragment key={`${item.label}-${index}`}>
                <HStack justifyContent="space-between">
                  <Text color="neutral.dark">{item.label}</Text>
                  <Text>{item.value}</Text>
                </HStack>
                {index < content.headerInfoItems.length - 1 && (
                  <Box h="1px" bg="neutral.lightest" />
                )}
              </React.Fragment>
            ))}
          </VStack>
        </VStack>

        <VStack borderRadius={BoxRadius.sm} p="inline.xxxs" bg="neutral.white" space="stack.xxxs">
          <Text color="neutral.dark">Serviço</Text>

          <Text fontSize="lg" variant="semibold">
            {content.serviceName}
          </Text>

          <Box h="1px" bg="neutral.lightest" />

          <VStack space="stack.nano">
            {content.detailsInfoItems.map((item, index) => (
              <React.Fragment key={`${item.label}-${index}`}>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="neutral.dark">
                    {item.label}
                  </Text>
                  <Text>{item.value}</Text>
                </HStack>
                {index < content.detailsInfoItems.length - 1 && (
                  <Box h="1px" bg="neutral.lightest" />
                )}
              </React.Fragment>
            ))}
          </VStack>

          {content.observation && (
            <Text fontSize="xs" color="neutral.medium">
              {content.observation}
            </Text>
          )}

          <TermsAndDocuments
            items={[
              {
                title: content.documentTitle || termData.termsFilename || 'Documento',
                url: termData.termsLinkUrl,
                onPress: () => handleDownloadTerm(termData),
              },
            ]}
          />
        </VStack>
      </VStack>

      <HStack
        justifyContent="space-between"
        borderTopColor="neutral.lightest"
        borderTopWidth={1}
        p="inset.xs">
        <Button variant="secondary" w="48%" onPress={() => handleRefuse(termData)}>
          {content.refuseButtonLabel}
        </Button>
        <Button
          w="48%"
          onPress={() => handleAccept(termData)}
          variant={content.acceptButtonVariant}>
          {content.acceptButtonLabel}
        </Button>
      </HStack>
    </View>
  );
};
